import { useEffect, useState } from "react";
import axios from "../../api/axios";
import SidebarClient from "../../components/SidebarClient";

export default function AutoReply() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState({
    enabled: false,
    ignore_groups: true,
    ignore_commands: true,
  });

  const [rules, setRules] = useState([]);

  const [newRule, setNewRule] = useState({
    match_type: "CONTAINS",
    keyword: "",
    reply_text: "",
    priority: 100,
    is_active: true,
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, rRes] = await Promise.all([
        axios.get("/auto-reply/settings"),
        axios.get("/auto-reply/rules"),
      ]);
      setSettings(sRes.data);
      setRules(rRes.data);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to load auto reply");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const saveSettings = async (next) => {
    setSaving(true);
    try {
      await axios.put("/auto-reply/settings", next);
      setSettings(next);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to save settings");
    }
    setSaving(false);
  };

  const addRule = async () => {
    if (!newRule.keyword.trim() || !newRule.reply_text.trim()) {
      alert("Keyword and reply are required");
      return;
    }

    try {
      await axios.post("/auto-reply/rules", newRule);
      setNewRule({ match_type: "CONTAINS", keyword: "", reply_text: "", priority: 100, is_active: true });
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to add rule");
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await axios.delete(`/auto-reply/rules/${id}`);
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to delete rule");
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarClient />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">Auto Reply</h1>
        <p className="text-gray-500 mb-6">Reply only to incoming messages (DM). Group messages are ignored by default.</p>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Settings</h2>
                <span className={`text-xs font-black px-3 py-1 rounded-full ${settings.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                  {settings.enabled ? "ENABLED" : "DISABLED"}
                </span>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-800">Auto Reply</p>
                    <p className="text-sm text-gray-500">Enable auto replies for incoming messages</p>
                  </div>
                  <button
                    disabled={saving}
                    onClick={() => saveSettings({ ...settings, enabled: !settings.enabled })}
                    className={`w-14 h-8 rounded-full transition-colors ${settings.enabled ? "bg-green-500" : "bg-gray-300"}`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.enabled ? "translate-x-7" : "translate-x-1"}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-800">Ignore Groups</p>
                    <p className="text-sm text-gray-500">Don’t reply in group chats</p>
                  </div>
                  <button
                    disabled={saving}
                    onClick={() => saveSettings({ ...settings, ignore_groups: !settings.ignore_groups })}
                    className={`w-14 h-8 rounded-full transition-colors ${settings.ignore_groups ? "bg-green-500" : "bg-gray-300"}`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.ignore_groups ? "translate-x-7" : "translate-x-1"}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-800">Ignore Commands</p>
                    <p className="text-sm text-gray-500">Ignore messages starting with /</p>
                  </div>
                  <button
                    disabled={saving}
                    onClick={() => saveSettings({ ...settings, ignore_commands: !settings.ignore_commands })}
                    className={`w-14 h-8 rounded-full transition-colors ${settings.ignore_commands ? "bg-green-500" : "bg-gray-300"}`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.ignore_commands ? "translate-x-7" : "translate-x-1"}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Rules */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Rules</h2>

              <div className="space-y-3 mb-6">
                <div className="grid grid-cols-2 gap-3">
                  <select
                    className="border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                    value={newRule.match_type}
                    onChange={(e) => setNewRule({ ...newRule, match_type: e.target.value })}
                  >
                    <option value="CONTAINS">Contains</option>
                    <option value="EXACT">Exact</option>
                  </select>
                  <input
                    type="number"
                    className="border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Priority (lower first)"
                    value={newRule.priority}
                    onChange={(e) => setNewRule({ ...newRule, priority: e.target.value })}
                  />
                </div>

                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Keyword (e.g. price)"
                  value={newRule.keyword}
                  onChange={(e) => setNewRule({ ...newRule, keyword: e.target.value })}
                />
                <textarea
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  rows={3}
                  placeholder="Reply text"
                  value={newRule.reply_text}
                  onChange={(e) => setNewRule({ ...newRule, reply_text: e.target.value })}
                />
                <button
                  onClick={addRule}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition"
                >
                  Add Rule
                </button>
              </div>

              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left">Match</th>
                      <th className="p-3 text-left">Keyword</th>
                      <th className="p-3 text-left">Reply</th>
                      <th className="p-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rules.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="p-6 text-center text-gray-400">
                          No rules yet
                        </td>
                      </tr>
                    ) : (
                      rules.map((r) => (
                        <tr key={r.id}>
                          <td className="p-3 font-bold text-gray-700">{r.match_type}</td>
                          <td className="p-3 font-mono">{r.keyword}</td>
                          <td className="p-3 text-gray-700">{String(r.reply_text).slice(0, 60)}{String(r.reply_text).length > 60 ? "..." : ""}</td>
                          <td className="p-3 text-center">
                            <button
                              onClick={() => deleteRule(r.id)}
                              className="bg-red-100 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-200 transition"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
